import { useMutation } from '@apollo/client';
import { getOperationDetails, useCommon } from '@facephi/inphinite-common';
import { Spinner } from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ModalManagementRejectStyles,
  ModalManagementRejectWrapper,
  ReviewTextArea,
  SpinnerAbsolute,
} from './Styles';
import { RejectDto, rejectSchema } from '../../state/model';
import { createReview } from '../../state/mutations';

type Props = {
  show: boolean;
  onChangeShow(show: boolean): void;
  operationId: string;
};

export const ModalReject = ({ show, onChangeShow, operationId }: Props) => {
  const { t } = useTranslation();
  const {
    tenant: { id: tenantId },
  } = useCommon();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(rejectSchema),
    defaultValues: {
      rejectNote: '',
    },
  });

  const loading = false;
  const [saveReview] = useMutation(createReview);

  const onSubmit = async (rejection: RejectDto) => {
    const response = await saveReview({
      variables: {
        input: {
          checks: [
            {
              name: 'sediaData',
              isChecked: false,
            },
            {
              name: 'debtData',
              isChecked: false,
            },
            {
              name: 'evidenceData',
              isChecked: false,
            },
          ],
          comment: rejection.rejectNote,
          approved: false,
        },
        operationId: operationId,
      },
      refetchQueries: [
        {
          query: getOperationDetails,
          variables: { operationId, tenantId },
        },
      ],
    });

    if (response.data) {
      setValue('rejectNote', '');
      onChangeShow(!show);
    }
  };

  return (
    <ModalManagementRejectStyles
      show={show}
      onChangeShow={onChangeShow}
      header={{
        title: t('Reject reason'),
      }}
      footer={{
        closeButtonLabel: t('Cancel'),
        submitButtonLabel: t('Send'),
        onSubmitButton: () => !loading && handleSubmit(onSubmit)(),
      }}
      isForm
      hasPadding
    >
      <ModalManagementRejectWrapper
        flexDirection="column"
        rowGap="2.4"
        flex="1"
      >
        {loading && (
          <SpinnerAbsolute>
            <Spinner />
          </SpinnerAbsolute>
        )}

        <ReviewTextArea
          label={t('Add a reason')}
          {...register('rejectNote')}
          rows={10}
          iconRight="PencilSimpleLine"
          testId="reject-input"
          errorLabel={
            errors.rejectNote?.message && t(errors.rejectNote.message)
          }
        />
      </ModalManagementRejectWrapper>
    </ModalManagementRejectStyles>
  );
};
